import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listTemplates, applyTemplate } from "@/lib/templates.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Boxes, ShoppingBag, Repeat, Wrench, Undo2, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/templates")({
  component: TemplatesPage,
});

const CATEGORY_ICON: Record<string, any> = {
  dtc: ShoppingBag,
  b2b: Boxes,
  subscription: Repeat,
  kitting: Wrench,
  returns: Undo2,
};

function TemplatesPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listTemplates);
  const applyFn = useServerFn(applyTemplate);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ["workflow-templates", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const apply = useMutation({
    mutationFn: (templateId: string) => applyFn({ data: { tenantId: tenantId!, templateId } }),
    onSuccess: (res) => {
      toast.success(
        `Template applied · ${res.created.billing_rules} billing rules, ${res.created.hold_rules} hold rules` +
          (res.created.skipped ? ` (${res.created.skipped} already existed)` : ""),
      );
      qc.invalidateQueries({ queryKey: ["workflow-templates", tenantId] });
      setConfirmId(null);
      setPreviewId(null);
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to apply template"),
  });

  const previewTpl = templates?.find((t) => t.id === previewId) ?? null;

  return (
    <>
      <PageHeader
        title="Workflow Templates"
        description="One-click bootstrap for common 3PL operating models. Idempotent — safe to re-apply."
      />
      <div className="p-4 sm:p-6 space-y-6">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-44 w-full" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(templates ?? []).map((t) => {
              const Icon = CATEGORY_ICON[t.category] ?? Boxes;
              return (
                <div key={t.id} className="border rounded-lg bg-card p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="rounded-md border bg-background p-2"><Icon className="h-5 w-5" /></div>
                      <div className="font-semibold">{t.name}</div>
                    </div>
                    {t.applied_at && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Applied
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mt-3 flex-1">{t.tagline}</div>
                  <div className="text-xs text-muted-foreground mt-3">
                    {t.billing_rules.length} billing rules · {t.hold_rules.length} hold rules
                  </div>
                  {t.applied_at && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Last applied {new Date(t.applied_at).toLocaleDateString()}
                    </div>
                  )}
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPreviewId(t.id)}>Preview</Button>
                    <Button size="sm" onClick={() => setConfirmId(t.id)}>
                      {t.applied_at ? "Re-apply" : "Apply"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Sheet open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)}>
        <SheetContent className="overflow-y-auto sm:max-w-xl">
          {previewTpl && (
            <>
              <SheetHeader>
                <SheetTitle>{previewTpl.name}</SheetTitle>
                <SheetDescription>{previewTpl.description}</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                <div>
                  <div className="text-sm font-semibold mb-2">Billing rules</div>
                  <div className="border rounded-md divide-y">
                    {previewTpl.billing_rules.map((br, i) => (
                      <div key={i} className="px-3 py-2 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm truncate">{br.description}</div>
                          <Badge variant="outline" className="mt-1 text-xs">{br.rule_type.replace(/_/g, " ")}</Badge>
                        </div>
                        <div className="text-sm font-mono tabular-nums">
                          ${(br.unit_price_cents / 100).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold mb-2">Hold release rules</div>
                  <div className="border rounded-md divide-y">
                    {previewTpl.hold_rules.map((hr, i) => (
                      <div key={i} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                        <Badge variant="outline">{hr.hold_reason}</Badge>
                        <span className="text-muted-foreground">
                          {hr.trigger.replace(/_/g, " ")}
                          {hr.params?.hours ? ` · ${hr.params.hours}h` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setPreviewId(null)}>Close</Button>
                  <Button onClick={() => setConfirmId(previewTpl.id)}>
                    {previewTpl.applied_at ? "Re-apply" : "Apply"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply this template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create the listed billing and hold-release rules in the current tenant.
              Existing rules with matching names are skipped (no duplicates).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={apply.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); if (confirmId) apply.mutate(confirmId); }}
              disabled={apply.isPending}
            >
              {apply.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Apply template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}